import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUpload } from '../context/UploadContext';
import { notify } from '../utils/notifications';
import '../styles/CaseManagement.css';
import api, { facilityApi } from '../api/api';
import OnboardingBanner from './OnboardingBanner';
import { useCases } from '../hooks/useCases';
import { useCaseMetrics } from '../hooks/useCaseMetrics';
import { useFacilityMembers } from '../hooks/useFacilityMembers';
import CaseListMetrics from './caselist/CaseListMetrics';
import CaseAgeBadge, { shouldShowAge } from './caselist/CaseAgeBadge';
import AdmissionStatusPicker from './caselist/AdmissionStatusPicker';
import ConfirmDialog from './workflows/shared/ConfirmDialog';
import { getPatientDisplayName, getInsuranceProvider } from './caselist/caseFields';
import CaseFilters from './caselist/CaseFilters';
import { applyCaseFilters, EMPTY_FILTERS, hasActiveFilters, insuranceOptions } from './caselist/caseFilterUtils';

export function countActiveCriticalFlags(redFlags) {
    const flags = redFlags?.flags;
    if (!Array.isArray(flags)) return 0;
    // A "rejected" flag is a confirmed false positive and no longer counts.
    // "approved" critical flags still count — the issue is real, just acknowledged.
    return flags.filter(f => f?.severity === 'critical' && f?.status !== 'rejected').length;
}

// B2: legacy cases predate assignedTo; the owner is the assignee until B3 reassigns.
const assigneeOf = (c) => c.assignedTo ?? c.ownerUid ?? c.userId ?? null;
const assigneeLabel = (c, members) => c.assignedToName ?? members[assigneeOf(c)]?.name ?? 'Unassigned';

export const caseHref = (caseItem, uid) => (
    caseItem.ownerUid && caseItem.ownerUid !== uid
        ? `/case/${caseItem.id}?owner=${caseItem.ownerUid}`
        : `/case/${caseItem.id}`
);

function CaseManagement() {
    const { user, facilityId, isFacilityAdmin, profileReady } = useAuth();
    const navigate = useNavigate();
    const uploadCtx = useUpload();
    const [sortBy, setSortBy] = useState('lastModifiedDesc');
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Case ids with a reassign in flight; the row's picker is locked meanwhile.
    const [reassigning, setReassigning] = useState(() => new Set());

    const facilityMode = Boolean(facilityId);
    const [scope, setScope] = useState('mine');
    // The role arrives with the profile; set the default once it is known.
    useEffect(() => {
        if (profileReady) setScope(isFacilityAdmin ? 'team' : 'mine');
    }, [profileReady, isFacilityAdmin]);
    const [assigneeFilter, setAssigneeFilter] = useState('all');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const clearAllFilters = () => {
        setFilters(EMPTY_FILTERS);
        setAssigneeFilter('all');
    };
    const { cases, loading: isLoading, error: casesError } = useCases(user?.uid, { facilityId: facilityId ?? null });
    const { members } = useFacilityMembers(facilityMode ? facilityId : null);
    const metrics = useCaseMetrics(cases);

    if (casesError) {
        // Surface to console — Sentry catches it via the global ErrorBoundary.
        // eslint-disable-next-line no-console
        console.error('useCases listener error:', casesError);
    }

    const handleNewCase = () => {
        navigate('/case/new', { replace: true });
    };

    const handleEditCase = (caseItem) => navigate(caseHref(caseItem, user?.uid));

    const isProcessing = (caseItem) => {
        // Case is processing if status.condition is 'working'
        if (caseItem.status?.condition === 'working') {
            return true;
        }
        // Or if upload context is actively uploading for this case
        if (uploadCtx.caseId === caseItem.id) {
            const hasActiveFile = uploadCtx.files.some(
                f => f.status === 'pending' || f.status === 'uploading' || f.status === 'stalled'
            );
            if (hasActiveFile) {
                return true;
            }
        }
        return false;
    };

    const handleDeleteClick = (caseItem) => {
        setDeleteTarget(caseItem);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const formData = new FormData();
            formData.append('case_id', deleteTarget.id);

            // Call the backend endpoint to delete the case and its file
            await api.delete('/case/delete', {
                data: formData
            });
            // No local state mutation: useCases onSnapshot will receive the
            // delete event and update `cases` automatically.
        } catch (error) {
            console.error('Error deleting case:', error);
            notify.error('Failed to delete case. Please try again.');
        } finally {
            setDeleteTarget(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteTarget(null);
    };

    const onReassign = async (caseItem, assignedTo) => {
        if (assignedTo === (assigneeOf(caseItem) ?? '')) return;
        setReassigning((s) => new Set(s).add(caseItem.id));
        try {
            await facilityApi.reassignCase(facilityId, caseItem.id, { owner_uid: caseItem.ownerUid, assigned_to: assignedTo });
            notify.success(`Assigned to ${members[assignedTo]?.name || assignedTo}`);
            // No local state change: the collection-group onSnapshot delivers the update.
        } catch (err) {
            notify.error(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to reassign case');
        } finally {
            setReassigning((s) => { const next = new Set(s); next.delete(caseItem.id); return next; });
        }
    };

    // useCaseMetrics intentionally consumes the unfiltered `cases` array so its
    // aggregate counts and the closed-trend chart stay correct. The render layer
    // filters archived cases out of the visible list.
    const visibleCases = cases.filter((c) => !c.archived);

    // Facility mode: "mine" keeps cases assigned to me (even when a colleague owns
    // them), "team" keeps every case; the assignee filter narrows either scope.
    const scopedCases = facilityMode
        ? visibleCases.filter((c) => (
            (scope === 'team' || assigneeOf(c) === user?.uid)
            && (assigneeFilter === 'all' || assigneeOf(c) === assigneeFilter)
        ))
        : visibleCases;

    const filteredCases = applyCaseFilters(scopedCases, filters);
    const filtersActive = hasActiveFilters(filters);
    const insuranceChoices = insuranceOptions(scopedCases, filters.insurances);

    const sortedCases = (() => {
        const copy = [...filteredCases];
        if (sortBy === 'patientNameAsc') {
            return copy.sort((a, b) => {
                const nameA = (getPatientDisplayName(a.structuredInfo)).toLowerCase();
                const nameB = (getPatientDisplayName(b.structuredInfo)).toLowerCase();
                return nameA.localeCompare(nameB);
            });
        }
        if (sortBy === 'admissionStatus') {
            const order = [
                'In progress',
                'Submitted to Insurance',
                'Insurance Approved',
                'Insurance Denied',
                'Rejected',
                'Admitted',
                'Closed',
            ];
            const indexOf = (label) => {
                const norm = (label || 'In progress').toString();
                const idx = order.indexOf(norm);
                return idx === -1 ? order.indexOf('In progress') : idx;
            };
            return copy.sort((a, b) => indexOf(a.admission_status) - indexOf(b.admission_status));
        }
        if (sortBy === 'createdDesc') {
            return copy.sort((a, b) => {
                const dateA = a.createdAt?.toMillis?.() ?? 0;
                const dateB = b.createdAt?.toMillis?.() ?? 0;
                return dateB - dateA;
            });
        }
        if (sortBy === 'caseAgeDesc') {
            return copy.sort((a, b) => {
                const msA = a.createdAt?.toMillis?.();
                const msB = b.createdAt?.toMillis?.();
                // Cases without a creation date sort to the bottom
                if (msA == null && msB == null) return 0;
                if (msA == null) return 1;
                if (msB == null) return -1;
                return msA - msB; // ascending creation date = oldest (most urgent) first
            });
        }
        if (sortBy === 'insuranceAsc') {
            return copy.sort((a, b) => {
                const insA = getInsuranceProvider(a.structuredInfo);
                const insB = getInsuranceProvider(b.structuredInfo);
                // Cases without insurance sort to the bottom
                if (!insA && !insB) return 0;
                if (!insA) return 1;
                if (!insB) return -1;
                return insA.toLowerCase().localeCompare(insB.toLowerCase());
            });
        }
        // Default: last modified, newest first
        return copy.sort((a, b) => {
            const dateA = a.last_modified_date?.toDate() || new Date(0);
            const dateB = b.last_modified_date?.toDate() || new Date(0);
            return dateB - dateA;
        });
    })();

    return (
        <div className="case-management">
            <div className="case-management-header">
                <h1>{facilityMode ? 'Cases' : 'My Cases'}</h1>
                <div className="header-actions">
                    <button
                        className="new-case-button"
                        onClick={handleNewCase}
                    >
                        Create New Case
                    </button>
                </div>
            </div>

            {facilityMode && (
                <div className="case-scope" role="group" aria-label="Case scope">
                    <label className={`case-scope-option${scope === 'mine' ? ' is-active' : ''}`}>
                        <input type="radio" name="case-scope" value="mine" checked={scope === 'mine'} onChange={() => setScope('mine')} />
                        My cases
                    </label>
                    <label className={`case-scope-option${scope === 'team' ? ' is-active' : ''}`}>
                        <input type="radio" name="case-scope" value="team" checked={scope === 'team'} onChange={() => setScope('team')} />
                        Team cases
                    </label>
                </div>
            )}

            <OnboardingBanner />

            <CaseListMetrics metrics={metrics} loading={isLoading} />

            {facilityMode && casesError && (
                <div className="case-list-error" role="alert">
                    Could not load your agency's cases. Reload the page; if it persists, contact billing@censuszen.com.
                </div>
            )}

            {(visibleCases.length > 0) && (
                <>
                    <CaseFilters
                        filters={filters}
                        onChange={setFilters}
                        insuranceOptions={insuranceChoices}
                        members={facilityMode && scope === 'team' ? members : undefined}
                        assignee={assigneeFilter}
                        onAssigneeChange={setAssigneeFilter}
                        onClearAll={clearAllFilters}
                    />
                    <div className="case-list-controls">
                        <label className="sort-control">
                            <span className="sort-label">Sort:</span>
                            <select
                                className="sort-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="lastModifiedDesc">Last modified (newest first)</option>
                                <option value="createdDesc">Created (newest first)</option>
                                <option value="caseAgeDesc">Filing window (most elapsed first)</option>
                                <option value="patientNameAsc">Patient Name (A–Z)</option>
                                <option value="admissionStatus">Admission status</option>
                                <option value="insuranceAsc">Insurance (A–Z)</option>
                            </select>
                        </label>
                    </div>
                </>
            )}

            <div className="cases-list" aria-busy={isLoading || undefined}>
                {isLoading ? (
                    [0, 1, 2].map((i) => (
                        <div key={i} className="case-item case-item--skeleton" data-testid="case-row-skeleton" aria-hidden="true" />
                    ))
                ) : visibleCases.length === 0 ? (
                    <div className="no-cases">
                        <p>You haven't created any cases yet.</p>
                        <button onClick={handleNewCase}>Create your first case</button>
                    </div>
                ) : facilityMode && scopedCases.length === 0 ? (
                    <div className="no-cases">
                        <p>No cases match this view.</p>
                        {assigneeFilter !== 'all' && (
                            <button onClick={clearAllFilters}>Clear filters</button>
                        )}
                    </div>
                ) : filtersActive && filteredCases.length === 0 ? (
                    <div className="no-cases">
                        <p>No cases match these filters.</p>
                        <button onClick={clearAllFilters}>Clear filters</button>
                    </div>
                ) : (
                    sortedCases.map(caseItem => {
                        const isOwner = facilityMode ? caseItem.ownerUid === user?.uid : true;
                        // B4: a colleague's case opens via ?owner= and its status is editable;
                        // only a row with no resolvable owner stays read-only.
                        const unattributed = facilityMode && !caseItem.ownerUid;
                        return (
                        <div key={caseItem.id} className="case-item">
                            <div className="case-info">
                                <div className="case-title-row">
                                    <h3>{getPatientDisplayName(caseItem.structuredInfo)}</h3>
                                    {(() => {
                                        const criticalCount = countActiveCriticalFlags(caseItem.redFlags);
                                        if (criticalCount === 0) return null;
                                        const label = `${criticalCount} critical red flag${criticalCount === 1 ? '' : 's'}`;
                                        return (
                                            <span
                                                className="case-critical-flag"
                                                role="img"
                                                aria-label={label}
                                                title={label}
                                            >
                                                🚩
                                            </span>
                                        );
                                    })()}
                                    <CaseAgeBadge
                                        createdAt={caseItem.createdAt}
                                        admissionStatus={caseItem.admission_status}
                                        archived={false}
                                    />
                                </div>
                                <div className="case-meta">
                                    {!unattributed ? (
                                        <AdmissionStatusPicker
                                            caseId={caseItem.id}
                                            currentStatus={caseItem.admission_status}
                                            archived={false}
                                            ownerUid={caseItem.ownerUid}
                                        />
                                    ) : (
                                        <span className="admission-pill admission-pill--readonly">
                                            {caseItem.admission_status || 'In progress'}
                                        </span>
                                    )}
                                    {getInsuranceProvider(caseItem.structuredInfo) && (
                                        <>
                                            <span className="meta-separator">•</span>
                                            Insurance: {getInsuranceProvider(caseItem.structuredInfo)}
                                        </>
                                    )}
                                    {shouldShowAge(caseItem.createdAt, caseItem.admission_status, false) && (
                                        <>
                                            <span className="meta-separator">•</span>
                                            Created: {new Date(caseItem.createdAt.toMillis()).toLocaleDateString()}
                                        </>
                                    )}
                                    <span className="meta-separator">•</span>
                                    Last modified: {caseItem.last_modified_date ?
                                        new Date(caseItem.last_modified_date.toDate()).toLocaleString() :
                                        'N/A'}
                                    {facilityMode && (
                                        <>
                                            <span className="meta-separator">•</span>
                                            {isFacilityAdmin && scope === 'team' && caseItem.ownerUid ? (
                                                <label className="case-assignee">
                                                    Assigned to{' '}
                                                    <select
                                                        className="case-assignee-select"
                                                        aria-label={`Assignee for ${getPatientDisplayName(caseItem.structuredInfo)}`}
                                                        value={assigneeOf(caseItem) ?? ''}
                                                        disabled={reassigning.has(caseItem.id)}
                                                        onChange={(e) => onReassign(caseItem, e.target.value)}
                                                    >
                                                        {Object.entries(members).map(([uid, m]) => (
                                                            <option key={uid} value={uid}>{m.name || uid}</option>
                                                        ))}
                                                        {assigneeOf(caseItem) && !members[assigneeOf(caseItem)] && (
                                                            // Current assignee is not a member (left or offboarded):
                                                            // show them as a disabled placeholder, never a blank.
                                                            <option value={assigneeOf(caseItem)} disabled>{assigneeLabel(caseItem, members)}</option>
                                                        )}
                                                    </select>
                                                </label>
                                            ) : (
                                                <span className="case-assignee">Assigned to {assigneeLabel(caseItem, members)}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="case-actions">
                                <button
                                    onClick={() => handleEditCase(caseItem)}
                                    className="edit-button"
                                    disabled={unattributed}
                                    title={unattributed ? 'This case has no resolvable owner' : undefined}
                                >
                                    Open
                                </button>
                                {isOwner && (
                                    <button
                                        onClick={() => handleDeleteClick(caseItem)}
                                        className="delete-button"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })
                )}
            </div>

            {deleteTarget && (
                <ConfirmDialog
                    open={Boolean(deleteTarget)}
                    title={
                        isProcessing(deleteTarget)
                            ? 'Delete case while processing?'
                            : 'Delete this case?'
                    }
                    message={
                        isProcessing(deleteTarget)
                            ? 'This case is still processing. Deleting now will discard the extraction in progress.'
                            : 'Are you sure you want to delete this case?'
                    }
                    confirmLabel={
                        isProcessing(deleteTarget)
                            ? 'Delete anyway'
                            : 'Delete'
                    }
                    danger={true}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
}

export default CaseManagement;
